import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import Icon from 'dpl/common/components/Icon';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import { GENDERS } from 'dpl/shared/constants/breeder_app';
import SmartImage from 'dpl/components/SmartImage';
import ImagePlaceholder from 'dpl/components/MediaGrid/ImagePlaceholder';
import { BREED_HEALTH_TESTING_GUIDE_PATH } from 'dpl/shared/constants/urls';

export default function HealthTestingMobileMenu({
  dog,
  isVisible,
  onCloseClick,
  portalMountRef
}) {
  const healthTesting = dog.health_testing;
  const parentTitle = dog.gender === GENDERS.FEMALE ? 'mom' : 'dad';

  const dogHealthTestResults = useMemo(
    () =>
      (dog.health_test_results || []).filter(
        htr => (htr.health_test_result_documents_attributes || []).length !== 0
      ),
    [dog]
  );

  return (
    <FixedTooltipPortal
      className="HealthTestingMobileMenu no-scrollbars ph1"
      isVisible={isVisible}
      onCloseClick={onCloseClick}
      portalMountRef={portalMountRef}
      style={{ maxHeight: '85vh' }}
      shouldHideCloseButton
    >
      <div>
        <button
          type="button"
          onClick={onCloseClick}
          className="absolute top-1 left-1 black-50 z-999"
        >
          <Icon
            name="fetch-chevron-left"
            className="HealthTestingMobileMenu__backButton ba b--light-gray br-100 bg-white"
            height="32px"
            width="32px"
          />
        </button>
        <h4 className="font-24 lh-title tc pt1 mb10">Health tests</h4>
      </div>
      <div className="aspect-ratio--8x5 relative">
        <div className="aspect-ratio--object">
          {dog.profile_photo_url ? (
            <SmartImage
              Tag="div"
              className="br3 w-100 h-100 cover bg-center"
              loadingClass="bg-light-gray"
              src={dog.profile_photo_url}
              crop
              lazy
            />
          ) : (
            <ImagePlaceholder className="br3" />
          )}
        </div>
      </div>
      <div className="pv4 ph2">
        <h6 className="font-20 lh-title mb1">
          {dog.name}, {parentTitle}
        </h6>
        <p className="secondary font-16">{dog.breed_name}</p>
      </div>
      <div className="w-100 font-14 bg-teal-light br3 ph4 pv3 mb6">
        <div>
          <p className="fw-medium green-dark-900 mb1">{healthTesting.title}</p>
          <p>
            {dog.name} meets Good Dog’s health testing requirements for the{' '}
            {dog.health_tier_title} level.&nbsp;
            <a
              className="underline"
              target="_blank"
              rel="noreferrer noopener"
              href={`/breeds/${dog.breed_slug}${BREED_HEALTH_TESTING_GUIDE_PATH}`}
            >
              Learn more
            </a>
          </p>
        </div>
      </div>
      {dogHealthTestResults.map(htr => (
        <div className="mb6 font-16" key={htr.id}>
          <p className="fw-medium mb2">{htr.health_test_name}</p>
          {htr.description && (
            <p className="mb2 secondary">{htr.description}</p>
          )}
          {htr.health_test_result_documents_attributes?.map(doc => (
            <a
              key={doc.id}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="stone-700 flex items-center"
            >
              <span className="underline">
                View {doc.is_file ? 'document' : 'results'}
              </span>
              <Icon
                name="fetch-external"
                width="20px"
                height="20px"
                className="ml1 secondary"
              />
            </a>
          ))}
        </div>
      ))}
    </FixedTooltipPortal>
  );
}

HealthTestingMobileMenu.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  portalMountRef: PropTypes.object,
  dog: PropTypes.shape({
    breed_name: PropTypes.string,
    breed_slug: PropTypes.string,
    description: PropTypes.string,
    gender: PropTypes.oneOf(Object.values(GENDERS)),
    health_testing: PropTypes.shape({
      title: PropTypes.string,
      documented_health_tests: PropTypes.arrayOf(PropTypes.string)
    }),
    health_tier_title: PropTypes.string,
    health_test_results: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        description: PropTypes.string,
        health_test_id: PropTypes.number,
        health_test_name: PropTypes.string,
        health_test_result_documents: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
            is_file: PropTypes.bool,
            name: PropTypes.string,
            url: PropTypes.string
          })
        )
      })
    ),
    name: PropTypes.string,
    profile_photo_url: PropTypes.string
  }).isRequired
};

HealthTestingMobileMenu.defaultProps = {
  portalMountRef: {}
};
